import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "./hooks/useAuth";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { useEffect, Suspense, lazy } from "react";
import { BottomNavigation } from "./components/BottomNavigation";
import { AnimatePresence } from "framer-motion";
import { ThemeProvider } from "next-themes";

// Lazy loaded pages
const FeedNew = lazy(() => import("./pages/FeedNew"));
const FriendsTab = lazy(() => import("./pages/FriendsTab"));
const MessagesTab = lazy(() => import("./pages/MessagesTab"));
const ProfileTab = lazy(() => import("./pages/ProfileTab"));
const CreateBetWizard = lazy(() => import("./pages/CreateBetWizard"));
const FriendsComplete = lazy(() => import("./pages/FriendsComplete"));
const BetDetailNew = lazy(() => import("./pages/BetDetailNew"));
const AuthNew = lazy(() => import("./pages/AuthNew"));
const SettingsNew = lazy(() => import("./pages/SettingsNew"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 30,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const ScrollToTop = () => {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);
  return null;
};

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

// Page transition wrapper
import { motion } from 'framer-motion';

const pageVariants = {
  initial: { opacity: 0, x: 20 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -20 }
};

const pageTransition = {
  type: "tween" as const,
  ease: [0.25, 0.1, 0.25, 1] as [number, number, number, number],
  duration: 0.25
};

const AppContent = () => {
  const location = useLocation();
  const isAuthPage = location.pathname === '/auth';
  const isBetDetail = location.pathname.startsWith('/bet/');
  const showBottomNav = !isAuthPage && !isBetDetail;

  return (
    <div className="min-h-screen flex flex-col w-full bg-background">
      <main className="flex-1 pb-safe">
        <ScrollToTop />
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={location.pathname}
            initial="initial"
            animate="animate"
            exit="exit"
            variants={pageVariants}
            transition={pageTransition}
            className="h-full"
          >
            <Suspense fallback={<PageLoader />}>
              <Routes location={location}>
                {/* Main 5 tabs */}
                <Route path="/" element={<FeedNew />} />
                <Route path="/feed" element={<FeedNew />} />
                <Route path="/friends" element={<ProtectedRoute><FriendsTab /></ProtectedRoute>} />
                <Route path="/create" element={<CreateBetWizard />} />
                <Route path="/messages" element={<ProtectedRoute><FriendsComplete /></ProtectedRoute>} />
                <Route path="/profile" element={<ProtectedRoute><ProfileTab /></ProtectedRoute>} />
                
                {/* Other routes */}
                <Route path="/auth" element={<AuthNew />} />
                <Route path="/settings" element={<ProtectedRoute><SettingsNew /></ProtectedRoute>} />
                <Route path="/bet/:id" element={<BetDetailNew />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </motion.div>
        </AnimatePresence>
      </main>
      {showBottomNav && <BottomNavigation />}
    </div>
  );
};

const App = () => (
  <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={false}>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AuthProvider>
            <AppContent />
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ThemeProvider>
);

export default App;
